import { useMemo, useState } from 'react'

import * as Muicon from '@mui/icons-material'
import SearchIcon from '@mui/icons-material/Search'
import { Box, Grid, InputAdornment, Slider, TextField, Tooltip, Typography } from '@mui/material'

import { useThemeMode } from '../../../theme'
import { ArtCanvasIcon } from './ArtCanvasIcon'
import { TearoomIcon } from './TearoomIcon'
import { BartenderIcon } from './BartenderIcon'
import { BeerCheersIcon } from './BeerCheersIcon'
import { BeerGripIcon } from './BeerGripIcon'
import { CheckInDeskIcon } from './CheckInDeskIcon'
import { ChecklistIcon } from './ChecklistIcon'
import { ConMapIcon } from './ConMapIcon'
import { ConventionCrowdIcon } from './ConventionCrowdIcon'
import { CrouchingWolfIcon } from './CrouchingWolfIcon'
import { FursuitDance2Icon } from './FursuitDance2Icon'
import { FursuitDanceIcon } from './FursuitDanceIcon'
import { FursuitParadeIcon } from './FursuitParadeIcon'
import { GameShowIcon } from './GameShowIcon'
import { GroupPhoto3Icon } from './GroupPhoto3Icon'
import { GroupPhotoIcon } from './GroupPhotoIcon'
import { ParkingIcon } from './ParkingIcon'
import { PhotoboothIcon } from './PhotoboothIcon'
import { ReachingIcon } from './ReachingIcon'
import { RegistrationTerminalIcon } from './RegistrationTerminalIcon'
import { SnackBowlIcon } from './SnackBowlIcon'
import { StreamIcon } from './StreamIcon'
import { TakeMyMoneyIcon } from './TakeMyMoneyIcon'
import { VotingIcon } from './VotingIcon'
import { WolfPresentationIcon } from './WolfPresentationIcon'
import { WolfSilhouetteIcon } from './WolfSilhouetteIcon'

const customIcons = [
  ['ArtCanvasIcon', ArtCanvasIcon],
  ['TearoomIcon', TearoomIcon],
  ['BartenderIcon', BartenderIcon],
  ['BeerCheersIcon', BeerCheersIcon],
  ['BeerGripIcon', BeerGripIcon],
  ['CheckInDeskIcon', CheckInDeskIcon],
  ['ChecklistIcon', ChecklistIcon],
  ['ConMapIcon', ConMapIcon],
  ['ConventionCrowdIcon', ConventionCrowdIcon],
  ['CrouchingWolfIcon', CrouchingWolfIcon],
  ['FursuitDance2Icon', FursuitDance2Icon],
  ['FursuitDanceIcon', FursuitDanceIcon],
  ['FursuitParadeIcon', FursuitParadeIcon],
  ['GameShowIcon', GameShowIcon],
  ['GroupPhotoIcon', GroupPhotoIcon],
  ['GroupPhoto3Icon', GroupPhoto3Icon],
  ['ParkingIcon', ParkingIcon],
  ['PhotoboothIcon', PhotoboothIcon],
  ['ReachingIcon', ReachingIcon],
  ['RegistrationTerminalIcon', RegistrationTerminalIcon],
  ['SnackBowlIcon', SnackBowlIcon],
  ['StreamIcon', StreamIcon],
  ['TakeMyMoneyIcon', TakeMyMoneyIcon],
  ['VotingIcon', VotingIcon],
  ['WolfPresentationIcon', WolfPresentationIcon],
  ['WolfSilhouetteIcon', WolfSilhouetteIcon]
]

const allIcons = [...customIcons, ...Object.entries(Muicon)]

export const IconBrowser = () => {
  const { palette } = useThemeMode()
  const [query, setQuery] = useState('')
  const [iconSize, setIconSize] = useState(2)

  const cols = iconSize <= 1 ? 6 : iconSize <= 2 ? 3 : iconSize <= 3 ? 2 : 1
  const smCols = iconSize <= 1 ? 4 : iconSize <= 2 ? 2 : 1

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? allIcons.filter(([name]) => name.toLowerCase().includes(q)) : allIcons
  }, [query])

  return (
    <Box sx={{ width: '100%', maxWidth: '600px', margin: '0 auto', padding: '16px' }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search icons…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: palette.headerColor }} />
            </InputAdornment>
          )
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="caption" sx={{ color: palette.headerColor, whiteSpace: 'nowrap' }}>
          {filtered.length} icon{filtered.length !== 1 ? 's' : ''}
        </Typography>
        <Slider
          size="small"
          min={1}
          max={4}
          step={1}
          value={iconSize}
          onChange={(_, v) => setIconSize(v)}
          sx={{ color: palette.headerColor, flex: 1 }}
        />
      </Box>
      <Grid container spacing={1}>
        {filtered.map(([name, Icon]) => (
          <Grid item xs={cols} sm={smCols} key={name}>
            <Tooltip title={name} placement="top" arrow>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 4px',
                  borderRadius: '4px',
                  cursor: 'default',
                  '&:hover': { backgroundColor: palette.headerColor + '22' }
                }}
              >
                <Icon sx={{ color: palette.headerColor, fontSize: `${iconSize * 1.25}rem` }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.6rem',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    mt: 0.5,
                    wordBreak: 'break-all',
                    color: palette.textColorSecondary
                  }}
                >
                  {name}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}
